import { playerActions, selectPlayerState } from "app/store/slices/player";
import { useAppDispatch, useAppSelector } from "app/store/store";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MemoPlay from "../icons/Play";
import styles from "./podcast-player.module.scss";
import { Category, PodcastChapter, Stream } from "services-hooks/types";
import MemoPause from "../icons/Pause";
import { useInterval } from "react-use";
import MemoLoader from "../icons/Loader";
import { useSpacebarEvent } from "app/hooks/spacebar.hook";
import {
  getPillowChapterLive,
  postPillowListenV2ProgressChapter_id,
} from "../../../services-hooks/services";
import { useFavorites } from "app/hooks/favorites.hook";
import { useErrorToast } from "../toast/toast.component";
import { usePlayerContext } from "app/context/player.context";
import { PlayerFullScreen } from "./podcast-player-fullscreen.component";
import useAppAudio, { HTMLMediaState } from "app/hooks/audio.hook";
import { db } from "app/utils/db";
import { selectUserState, userActions } from "app/store/slices/user";
import { AdStream, TargetspotEventType } from "app/utils/audioAds";
import { useTranslate } from "app/hooks/lang";
import { useVirtualFastForward } from "app/hooks/vk-fast-forward.hook";
import { useFeaturesContext } from "app/context/features.context";

interface PodcastPlayerProps {
  podcastChapter: PodcastChapter;
  podcast: Category;
  playerOpen: boolean;
  setPlayerOpen: (open: boolean) => void;
  streams: Stream[];
  adStreams: AdStream[];
  streamsLoadError: boolean;
  refetchStreams: VoidFunction;
  streamsLoading: boolean;
  initialProgress: number;
  isOffline: boolean;
  sendTargetspotTimeUpdateEvent: (state: HTMLMediaState) => void;
  sendTargetspotTrackingEvent: (eventType: TargetspotEventType) => void;
  sendTargetspotStateUpdateEvent: (state: HTMLMediaState) => void;
}

export const PodcastPlayer: React.FC<PodcastPlayerProps> = ({
  podcastChapter,
  podcast,
  playerOpen,
  setPlayerOpen,
  streams,
  streamsLoading,
  adStreams,
  initialProgress,
  isOffline,
  sendTargetspotTimeUpdateEvent,
  sendTargetspotStateUpdateEvent,
  sendTargetspotTrackingEvent,
  refetchStreams,
  streamsLoadError,
}) => {
  const [categoryToAddToFavorites, setCategoryToAddToFavorites] =
    useState<Category | null>(null);
  const [categoryToRemoveFromFavorites, setCategoryToRemoveFromFavorites] =
    useState<Category | null>(null);
  const { isFavoriteCategory, addToFavorites, removeFromFavorites } =
    useFavorites();
  const t = useTranslate();
  const showStreamErrors = useErrorToast({
    content: t("playback-error"),
  });
  const showHttpStreamErrors = useErrorToast({
    content: t("http-error"),
  });

  const { session } = useAppSelector(selectUserState);

  const dispatch = useAppDispatch();
  const {
    playerEnabled,
    isPlaying,
    setPlayerEnabled,
    setDuration,
    setIsPlaying,
    setIsMediaLoading,
  } = usePlayerContext();
  const { podcastQueue } = useAppSelector(selectPlayerState);
  const isCurrentCategoryFavorite = isFavoriteCategory(podcast);
  const artwork = useMemo(
    () => [
      {
        src: podcastChapter.logo || podcast.logo!,
        sizes: "96x96",
        type: "image/png",
      },
      {
        src: podcastChapter.logo || podcast.logo!,
        sizes: "128x128",
        type: "image/png",
      },
      {
        src: podcastChapter.logo || podcast.logo!,
        sizes: "192x192",
        type: "image/png",
      },
      {
        src: podcastChapter.logo || podcast.logo!,
        sizes: "256x256",
        type: "image/png",
      },
      {
        src: podcastChapter.logo || podcast.logo!,
        sizes: "384x384",
        type: "image/png",
      },
      {
        src: podcastChapter.logo || podcast.logo!,
        sizes: "512x512",
        type: "image/png",
      },
    ],
    [podcastChapter.logo, podcast.logo]
  );
  useEffect(() => {
    if (categoryToAddToFavorites) {
      addToFavorites(categoryToAddToFavorites, isPlaying);
      setCategoryToAddToFavorites(null);
    }
  }, [categoryToAddToFavorites]);

  useEffect(() => {
    if (categoryToRemoveFromFavorites) {
      removeFromFavorites(categoryToRemoveFromFavorites, isPlaying);
      setCategoryToRemoveFromFavorites(null);
    }
  }, [categoryToRemoveFromFavorites]);

  const onPreviousTrack = useCallback(() => {
    if (podcastQueue.items.length > 0 && podcastQueue.activeIndex !== null) {
      if (podcastQueue.activeIndex > 0) {
        dispatch(
          playerActions.setPodcastQueue({
            items: podcastQueue.items,
            activeIndex: podcastQueue.activeIndex - 1,
          })
        );
      } else {
        dispatch(
          playerActions.setPodcastQueue({
            items: podcastQueue.items,
            activeIndex: podcastQueue.items.length - 1,
          })
        );
      }
    }
  }, [podcastQueue]);

  const onNextTrack = useCallback(() => {
    if (podcastQueue.items.length > 0 && podcastQueue.activeIndex !== null) {
      if (podcastQueue.activeIndex < podcastQueue.items.length - 1) {
        dispatch(
          playerActions.setPodcastQueue({
            items: podcastQueue.items,
            activeIndex: podcastQueue.activeIndex + 1,
          })
        );
      } else {
        dispatch(
          playerActions.setPodcastQueue({
            items: podcastQueue.items,
            activeIndex: 0,
          })
        );
      }
    }
  }, [podcastQueue]);

  const onFavoriteToggle = (enabled: boolean) => {
    if (enabled) {
      setCategoryToAddToFavorites(podcast);
    } else {
      setCategoryToRemoveFromFavorites(podcast);
    }
  };
  const onAdStreamEnd = () => {
    sendTargetspotTrackingEvent(TargetspotEventType.Complete);
    dispatch(userActions.setLastAdTimestamp(new Date()));
  };

  const onPlaybackError = (isHttps = true) => {
    if (!isHttps) {
      showHttpStreamErrors();
    } else {
      showStreamErrors();
    }
  };
  useEffect(() => {
    if (streamsLoadError) {
      showStreamErrors();
    }
  }, [streamsLoadError]);

  const onSeekBackward = () => {
    if (!state.activeAdStream) {
      controls.seek(state.time - 10);
    }
  };

  const onSeekForward = () => {
    if (!state.activeAdStream) {
      controls.seek(state.time + 10);
    }
  };
  const onPlay = () => {
    setPlayerEnabled(true);
  };
  const onStreamEnd = () => {
    onNextTrack();
  };

  const { audioElement, fallbackAudioElement, state, controls, audioRef } =
    useAppAudio(
      {
        title: podcastChapter.name,
        artist: podcast.name!,
        artwork: artwork,
        audioProps: {
          className: styles.audio_player,
          onPlay,
        },
        isFavorite: isCurrentCategoryFavorite,
        favoritesEnabled: !!session?.user,
        streamType: "podcast",
        streams,
        adStreams,
        streamsLoading,
        autoplay: playerEnabled,
        initialProgress,
        onStreamEnd,
        onNextTrack,
        onPreviousTrack,
        onFavoriteToggle,
        onAdStreamEnd,
        onPlaybackError,
        onSeekBackward,
        onSeekForward,
        permalink: podcast.permalink,
      },
      [podcastChapter.permalink]
    );

  const { arrowNavigationEnabled } = useFeaturesContext();
  useVirtualFastForward(
    () => {
      onSeekForward();
    },
    () => {
      onSeekBackward();
    },
    arrowNavigationEnabled
  );

  useEffect(() => {
    if (
      podcastQueue.activeIndex !== null &&
      podcastQueue.items[podcastQueue.activeIndex].permalink !==
        podcastChapter.permalink
    ) {
      dispatch(
        playerActions.selectTrack({
          podcast: {
            podcast,
            podcastChapter: podcastQueue.items[podcastQueue.activeIndex],
          },
          category: null,
        })
      );
    }
  }, [podcastQueue, podcastChapter, podcast]);

  const playerVariables = {
    "--image-background": `url(${podcastChapter.logo ?? podcast.logo})`,
  } as React.CSSProperties;

  useInterval(async () => {
    if (state.activeAdStream && state.playing) {
      sendTargetspotTimeUpdateEvent(state);
    }
    if (state.time > 0) {
      if (isOffline) {
        db.offlineChapters
          .update(podcastChapter.permalink!, {
            playbackProgress: state.time | 0,
          })
          .catch((e) => console.error(e));
      } else {
        if (
          session?.user &&
          podcastChapter.duration &&
          !audioRef.current?.paused
        ) {
          postPillowListenV2ProgressChapter_id(podcastChapter!.permalink!, {
            finished: (state.time | 0) > podcastChapter.duration - 15,
            seconds: state.time | 0,
          });
          try {
            await getPillowChapterLive(podcastChapter!.permalink!, {
              isPlaying: state.playing,
            });
          } catch (e) {}
        }
      }
    }
  }, 5000);

  useInterval(async () => {
    if (podcastChapter.permalink && state.playing) {
      try {
        await getPillowChapterLive(podcastChapter.permalink, {
          isPlaying: state.playing,
        });
      } catch (e) {}
    }
  }, 30000);

  const showOfflineError = useErrorToast({
    content: t("network.connection-lost"),
  });
  useEffect(() => {
    if (!window.navigator.onLine) {
      showOfflineError();
    }
  }, [podcastChapter.permalink]);

  useSpacebarEvent(controls.toggle);

  useEffect(() => {
    setIsPlaying(state.playing);
    if (podcastChapter.permalink) {
      getPillowChapterLive(podcastChapter.permalink, {
        isPlaying: state.playing,
      });
    }
    if (state.activeAdStream) {
      sendTargetspotStateUpdateEvent(state);
    }
  }, [state.playing]);

  useEffect(() => {
    setDuration(state.duration || null);
  }, [state.duration]);

  useEffect(() => {
    setIsMediaLoading(state.loading);
  }, [state.loading]);

  useEffect(() => {
    if (isPlaying && !state.playing) {
      controls.play();
    }
    if (!isPlaying && state.playing) {
      controls.pause();
    }
  }, [isPlaying]);
  const closePlayerCallback = useCallback(
    () => setPlayerOpen(false),
    [setPlayerOpen]
  );
  return (
    <>
      <div
        className={styles.content_wrapper}
        onClick={() => setPlayerOpen(true)}
        style={playerVariables}
        data-navigate-item
        tabIndex={0}
      >
        <div className={styles.content}>
          <div className={styles.track_info}>
            <img
              className={styles.cover_img}
              src={podcastChapter.logo ?? podcast.logo}
              alt=""
            />
            <div>
              <p className={styles.track_title}>{podcastChapter.name}</p>
            </div>
          </div>
          <div className={styles.track_controls}>
            <button
              className={styles.play_pause_icon}
              onClick={(e) => {
                if (streamsLoadError) {
                  refetchStreams();
                } else {
                  controls.toggle();
                }
                e.stopPropagation();
              }}
            >
              {!streamsLoadError && (streamsLoading || state.loading) ? (
                <MemoLoader />
              ) : state.playing ? (
                <MemoPause />
              ) : (
                <MemoPlay />
              )}
            </button>
          </div>
        </div>
      </div>
      {audioElement}
      {fallbackAudioElement}
      <PlayerFullScreen
        open={playerOpen}
        podcastChapter={podcastChapter}
        podcast={podcast}
        playerCssVariables={playerVariables}
        onClose={closePlayerCallback}
        playbackState={state}
        playbackControls={controls}
      />
    </>
  );
};
